"use client"

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { EmailAddressSuggestion, EmailReference, type SuggestionType } from "@/schema"
import { Button, Input, Popover, PopoverContent, PopoverTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import { Building2, ChevronDown, Contact as ContactIcon, ShieldUser, UserRound, UsersRound, X } from "lucide-react"
import { createPortal } from "react-dom"

import { useFetchEmailAddressSuggestions } from "@/hooks/api/email_addresses"

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.trim())
}

const SUGGESTION_TYPE_ICONS: Record<SuggestionType, React.ElementType> = {
  user: UserRound,
  client: ShieldUser,
  prospect: UsersRound,
  contact: ContactIcon,
  business: Building2,
}

const SUGGESTION_TYPE_LABELS: Record<SuggestionType, string> = {
  user: "Users",
  client: "Clients",
  prospect: "Prospects",
  contact: "Contacts",
  business: "Businesses",
}

const SUGGESTION_TYPE_ORDER: Record<SuggestionType, number> = {
  business: 0,
  contact: 1,
  client: 2,
  prospect: 3,
  user: 4,
}

function SuggestionIcon({ type }: { type: SuggestionType | null }) {
  const Icon = type ? SUGGESTION_TYPE_ICONS[type] : UserRound
  return <Icon className="text-muted-foreground size-4 shrink-0" />
}

interface EmailOption {
  email: string
  isPrimary?: boolean
}

function SelectedEmailTag({
  email,
  highlighted,
  pendingDelete,
  emailOptions,
  onRemove,
  onSwitch,
  readOnly = false,
}: {
  email: string
  highlighted?: boolean
  pendingDelete?: boolean
  emailOptions?: EmailOption[]
  onRemove?: () => void
  onSwitch?: (newEmail: string) => void
  readOnly?: boolean
}) {
  const [open, setOpen] = useState(false)
  const hasOptions = !readOnly && emailOptions && emailOptions.length > 1

  return (
    <div
      className={cn(
        "flex h-6 items-center rounded-md border text-sm/none",
        highlighted && !pendingDelete && "bg-secondary",
        pendingDelete && "border-destructive bg-destructive/10 border-dashed"
      )}
    >
      {hasOptions ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" className="hover:bg-secondary flex cursor-pointer items-center py-0 pr-0 pl-1.5">
              <span>{email}</span>
              <ChevronDown className="text-icon ml-0.5 size-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto min-w-[200px] p-1">
            <div className="flex flex-col">
              {emailOptions.map((opt) => (
                <button
                  key={opt.email}
                  type="button"
                  onClick={() => {
                    if (opt.email !== email) {
                      onSwitch?.(opt.email)
                    }
                    setOpen(false)
                  }}
                  className={cn(
                    "hover:bg-secondary flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                    opt.email === email && "bg-secondary"
                  )}
                >
                  <span>{opt.email}</span>
                  {opt.isPrimary && <span className="text-muted-foreground text-xs">(Primary)</span>}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <span className="pl-1.5">{email}</span>
      )}
      {!readOnly && (
        <button type="button" className="ml-0.5 pr-1" onClick={onRemove}>
          <X className="text-icon hover:text-destructive size-3" />
        </button>
      )}
    </div>
  )
}

interface PersonEntry {
  name: string | null
  type: SuggestionType | null
  emails: string[]
}

type VisibleItem = { kind: "person"; entry: PersonEntry } | { kind: "email"; email: string; entry: PersonEntry }

interface EmailReferenceInputProps {
  recipients: EmailReference[]
  label: string
  showCC?: boolean
  showBCC?: boolean
  toggleCC?: () => void
  toggleBCC?: () => void
  suggestions?: EmailAddressSuggestion[]
  excludeEmails?: Set<string>
  maxLength?: number
  onAddRecipient?: (recipient: EmailReference) => void
  onRemoveRecipient?: (email: string) => void
  onSwitchRecipient?: (oldEmail: string, newRecipient: EmailReference) => void
  className?: string
  readOnly?: boolean
}

export function EmailReferenceInput({
  recipients,
  label,
  toggleCC,
  toggleBCC,
  showCC,
  showBCC,
  suggestions: allSuggestions,
  excludeEmails,
  maxLength = 5,
  onAddRecipient,
  onRemoveRecipient,
  onSwitchRecipient,
  className,
  readOnly = false,
}: EmailReferenceInputProps) {
  const inputId = `email-${label.toLowerCase()}`
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState("")
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [highlightedTagIndex, setHighlightedTagIndex] = useState(-1)
  const [pendingDelete, setPendingDelete] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const [searchQuery, setSearchQuery] = useState("")
  const { data: globalSuggestionsData } = useFetchEmailAddressSuggestions(searchQuery)

  const existingEmails = useMemo(() => new Set(recipients.map((r) => r.email)), [recipients])

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearchQuery(inputValue), 200)
    return () => window.clearTimeout(timeout)
  }, [inputValue])

  const mergedSuggestions = useMemo(() => {
    const seen = new Set<string>()
    const suggestions = [...(allSuggestions ?? []), ...(globalSuggestionsData?.suggestions ?? [])]

    return suggestions.filter((suggestion) => {
      const key = suggestion.email.toLowerCase()
      if (seen.has(key)) return false

      seen.add(key)
      return true
    })
  }, [allSuggestions, globalSuggestionsData?.suggestions])

  const emailOptionsMap = useMemo(() => {
    if (!mergedSuggestions.length) return new Map<string, { name: string | null; options: EmailOption[] }>()
    const personMap = new Map<string, { name: string | null; options: EmailOption[] }>()
    for (const s of mergedSuggestions) {
      const key = s.personId ?? `${s.name ?? ""}::${s.type ?? ""}`
      const existing = personMap.get(key)
      if (existing) {
        if (!existing.options.some((o) => o.email === s.email)) {
          existing.options.push({ email: s.email, isPrimary: s.isPrimary })
        }
      } else {
        personMap.set(key, { name: s.name, options: [{ email: s.email, isPrimary: s.isPrimary }] })
      }
    }
    const result = new Map<string, { name: string | null; options: EmailOption[] }>()
    for (const person of personMap.values()) {
      if (person.options.length > 1) {
        for (const opt of person.options) {
          result.set(opt.email, person)
        }
      }
    }
    return result
  }, [mergedSuggestions])

  const filteredSuggestions = useMemo(() => {
    if (!mergedSuggestions.length) return []
    const query = inputValue.toLowerCase().trim()
    return mergedSuggestions.filter(
      (s) =>
        !existingEmails.has(s.email) &&
        !excludeEmails?.has(s.email) &&
        (!query || s.email.toLowerCase().includes(query) || (s.name && s.name.toLowerCase().includes(query)))
    )
  }, [mergedSuggestions, inputValue, existingEmails, excludeEmails])

  const personEntries = useMemo(() => {
    const map = new Map<string, PersonEntry>()
    for (const s of filteredSuggestions) {
      const key = s.personId ?? `${s.name ?? ""}::${s.type ?? ""}`
      const existing = map.get(key)
      if (existing) {
        existing.emails.push(s.email)
      } else {
        map.set(key, { name: s.name, type: s.type, emails: [s.email] })
      }
    }
    return Array.from(map.values())
  }, [filteredSuggestions])

  const groupedEntries = useMemo(() => {
    const groups: { type: SuggestionType; entries: PersonEntry[] }[] = []
    for (const entry of personEntries) {
      const type = entry.type ?? "user"
      let group = groups.find((g) => g.type === type)
      if (!group) {
        group = { type, entries: [] }
        groups.push(group)
      }
      group.entries.push(entry)
    }
    return groups.sort((a, b) => SUGGESTION_TYPE_ORDER[a.type] - SUGGESTION_TYPE_ORDER[b.type])
  }, [personEntries])

  const visibleItems = useMemo(() => {
    const items: VisibleItem[] = []
    for (const group of groupedEntries) {
      for (const entry of group.entries) {
        if (entry.emails.length === 1) {
          items.push({ kind: "person", entry })
        } else {
          for (const email of entry.emails) {
            items.push({ kind: "email", email, entry })
          }
        }
      }
    }
    return items
  }, [groupedEntries])

  const showTypedEmailFallback = inputValue.trim().length > 0 && visibleItems.length === 0
  const isDropdownVisible = showSuggestions && (visibleItems.length > 0 || showTypedEmailFallback)

  const portalContainer = useMemo(() => {
    if (!containerRef.current) return null
    return containerRef.current.closest("[role='dialog']") ?? document.body
  }, [isDropdownVisible]) // eslint-disable-line react-hooks/exhaustive-deps

  const updatePosition = () => {
    if (!containerRef.current || !portalContainer) return
    const containerRect = containerRef.current.getBoundingClientRect()
    const portalRect = portalContainer.getBoundingClientRect()
    setDropdownStyle({
      position: "absolute",
      top: containerRect.bottom - portalRect.top + 4,
      left: containerRect.left - portalRect.left,
      width: containerRect.width,
    })
  }

  useLayoutEffect(() => {
    if (!isDropdownVisible) return
    updatePosition()
  }, [isDropdownVisible, visibleItems]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isDropdownVisible) return
    window.addEventListener("scroll", updatePosition, true)
    return () => window.removeEventListener("scroll", updatePosition, true)
  }, [isDropdownVisible]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedIndex < 0 || !dropdownRef.current) return
    const item = dropdownRef.current.querySelector<HTMLElement>(`[data-index="${selectedIndex}"]`)
    item?.scrollIntoView({ block: "nearest" })
  }, [selectedIndex])

  const addRecipient = (recipient: EmailReference) => {
    onAddRecipient?.(recipient)
    setInputValue("")
    setSelectedIndex(-1)
    setShowSuggestions(false)
    setHighlightedTagIndex(-1)
    setPendingDelete(false)
    inputRef.current?.focus()
  }

  const addEmailIfValid = () => {
    const email = inputValue.trim()
    if (email && isValidEmail(email)) {
      addRecipient({ email })
    }
  }

  const handleSelectItem = (item: VisibleItem) => {
    if (item.kind === "email") {
      addRecipient({ email: item.email, name: item.entry.name ?? undefined })
    } else {
      addRecipient({ email: item.entry.emails[0], name: item.entry.name ?? undefined })
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const atInputStart = !inputValue && inputRef.current?.selectionStart === 0

    if (e.key === "ArrowLeft" && atInputStart && recipients.length > 0) {
      e.preventDefault()
      setPendingDelete(false)
      setHighlightedTagIndex((prev) => (prev > 0 ? prev - 1 : prev === -1 ? recipients.length - 1 : prev))
      return
    }

    if (e.key === "ArrowRight" && highlightedTagIndex >= 0) {
      e.preventDefault()
      setPendingDelete(false)
      setHighlightedTagIndex((prev) => (prev < recipients.length - 1 ? prev + 1 : -1))
      return
    }

    if (e.key !== "Backspace" && e.key !== "ArrowLeft" && e.key !== "ArrowRight" && highlightedTagIndex >= 0) {
      setHighlightedTagIndex(-1)
      setPendingDelete(false)
    }

    if (showSuggestions && visibleItems.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setSelectedIndex((prev) => (prev < visibleItems.length - 1 ? prev + 1 : 0))
        return
      }
      if (e.key === "ArrowUp") {
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : visibleItems.length - 1))
        return
      }
      if (e.key === "Enter" && selectedIndex >= 0) {
        e.preventDefault()
        handleSelectItem(visibleItems[selectedIndex])
        return
      }
    }

    if (e.key === "Enter" && inputValue) {
      e.preventDefault()
      addEmailIfValid()
    } else if (e.key === "Tab" && inputValue && isValidEmail(inputValue.trim())) {
      e.preventDefault()
      addEmailIfValid()
    } else if (e.key === "Backspace" && !inputValue && recipients.length > 0) {
      e.preventDefault()
      if (highlightedTagIndex >= 0 && pendingDelete) {
        onRemoveRecipient?.(recipients[highlightedTagIndex].email)
        setHighlightedTagIndex(-1)
        setPendingDelete(false)
      } else {
        setHighlightedTagIndex(recipients.length - 1)
        setPendingDelete(true)
      }
      return
    } else if (e.key === "Escape") {
      setShowSuggestions(false)
      setSelectedIndex(-1)
    }
  }

  const handleBlur = () => {
    setTimeout(() => {
      if (inputValue) {
        addEmailIfValid()
      }
      setShowSuggestions(false)
      setSelectedIndex(-1)
      setHighlightedTagIndex(-1)
      setPendingDelete(false)
    }, 150)
  }

  const renderDropdownItems = () => {
    let flatIndex = 0
    return groupedEntries.map((group) => (
      <div key={group.type}>
        <div className="text-muted-foreground px-3 pt-2 pb-1 text-xs font-medium">
          {SUGGESTION_TYPE_LABELS[group.type]}
        </div>
        {group.entries.map((entry) => {
          if (entry.emails.length === 1) {
            const idx = flatIndex++
            return (
              <button
                key={`${entry.name}-${entry.emails[0]}`}
                type="button"
                data-index={idx}
                className={cn(
                  "hover:bg-accent flex w-full items-center gap-3 px-3 py-1.5 text-sm",
                  idx === selectedIndex && "bg-accent"
                )}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addRecipient({ email: entry.emails[0], name: entry.name ?? undefined })}
              >
                <SuggestionIcon type={entry.type} />
                <div className="min-w-0 flex-1 text-left">
                  <div className="truncate font-medium">{entry.name}</div>
                  <div className="text-muted-foreground text-xs">
                    <span className="truncate">{entry.emails[0]}</span>
                  </div>
                </div>
              </button>
            )
          }

          const nameKey = `${entry.name ?? ""}::${entry.type ?? ""}`
          return (
            <div key={nameKey}>
              <div className="flex items-center gap-3 px-3 pt-1.5 pb-0.5">
                <SuggestionIcon type={entry.type} />
                <span className="truncate text-sm font-medium">{entry.name}</span>
              </div>
              {entry.emails.map((email) => {
                const idx = flatIndex++
                return (
                  <button
                    key={email}
                    type="button"
                    data-index={idx}
                    className={cn(
                      "hover:bg-accent flex w-full items-center gap-2 py-px pr-3 pl-10 text-sm",
                      idx === selectedIndex && "bg-accent"
                    )}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => addRecipient({ email, name: entry.name ?? undefined })}
                  >
                    <span className="text-muted-foreground truncate text-xs">{email}</span>
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>
    ))
  }

  return (
    <div className={cn("flex flex-none items-center gap-x-2", className)}>
      <label htmlFor={inputId} className="text-tertiary-foreground w-10 flex-none text-sm font-medium">
        {label}
      </label>
      <div ref={containerRef} className="relative flex h-full grow flex-wrap items-center gap-1.5">
        {recipients.map((recipient, index) => {
          const person = emailOptionsMap.get(recipient.email)
          const options = person?.options.filter((o) => !existingEmails.has(o.email) || o.email === recipient.email)
          return (
            <SelectedEmailTag
              key={`${recipient.email}-${index}`}
              email={recipient.email}
              highlighted={index === highlightedTagIndex}
              pendingDelete={index === highlightedTagIndex && pendingDelete}
              emailOptions={options && options.length > 1 ? options : undefined}
              onRemove={() => onRemoveRecipient?.(recipient.email)}
              onSwitch={(newEmail) =>
                onSwitchRecipient?.(recipient.email, { email: newEmail, name: person?.name ?? undefined })
              }
              readOnly={readOnly}
            />
          )
        })}
        {!readOnly && recipients.length < maxLength && (
          <Input
            ref={inputRef}
            id={inputId}
            type="text"
            value={inputValue}
            placeholder={recipients.length === 0 ? "email@example.com" : undefined}
            className="h-6 flex-1 rounded-none border-0 p-0 shadow-none focus-visible:min-w-[120px] focus-visible:ring-0"
            onChange={(e) => {
              setInputValue(e.target.value)
              setShowSuggestions(true)
              setSelectedIndex(0)
              setHighlightedTagIndex(-1)
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onFocus={() => setShowSuggestions(true)}
            onClick={() => setShowSuggestions(true)}
          />
        )}
        {isDropdownVisible &&
          portalContainer &&
          createPortal(
            <div
              ref={dropdownRef}
              className="bg-popover border-border z-50 max-h-80 overflow-y-auto rounded-md border shadow-md"
              style={dropdownStyle}
            >
              {showTypedEmailFallback ? (
                <button
                  type="button"
                  data-index={0}
                  className="bg-accent flex w-full items-center gap-3 px-3 py-2 text-sm"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addEmailIfValid()}
                >
                  <span className="font-medium">{inputValue.trim()}</span>
                </button>
              ) : (
                renderDropdownItems()
              )}
            </div>,
            portalContainer
          )}
      </div>
      <div className="flex items-center space-x-1">
        {!readOnly && toggleCC && (
          <Button
            size="iconXs"
            variant={showCC ? "secondary" : "ghost"}
            onClick={toggleCC}
            className="text-icon font-normal/none w-fit px-1.5"
          >
            Cc
          </Button>
        )}
        {!readOnly && toggleBCC && (
          <Button
            size="iconXs"
            variant={showBCC ? "secondary" : "ghost"}
            onClick={toggleBCC}
            className="text-icon font-normal/none w-fit px-1.5"
          >
            Bcc
          </Button>
        )}
      </div>
    </div>
  )
}
